import React, {useState, useMemo, useEffect} from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import s from './Settings.module.sass';
import { useSimpleTranslation } from '../../hooks/useTranslation';
import { getCountryNameByFlag } from '../../utils/countryNames';
import { resolveWallet } from '../../utils/walletResolver';
import person from '../../img/person-png.png';
import shield from '../../img/shield-check-fill-svgrepo-com.svg';
import key from '../../img/key.png';
import phone from '../../img/phone.png';
import email from '../../img/mail.png';
import android from '../../img/android.png';
import apple from '../../img/apple.png';
import windows from '../../img/windows.png';
import voskl from '../../img/voskl.svg';
import bank from '../../img/bank.png';
import qr1 from '../../img/qr1.png';

export const Settings = ({network, address, name, isNavbarCollapsed, selectedCrypto, cryptoOptions, currencyFormat, walletAddress, siteSettings}) => {
    const { t } = useSimpleTranslation(siteSettings);
    
    // Safe translation function
    const safeT = (key, fallback = '') => {
        if (typeof t === 'function') {
            return t(key, fallback);
        }
        return fallback;
    };

    // Translation function with crypto replacement
    const safeTC = (key, fallback = '') => {
        const text = safeT(key, fallback);
        return text.replace('{crypto}', selectedCrypto);
    };
    
    // Получаем список включенных банков из siteSettings, отсортированных по приоритету
    const enabledBanks = useMemo(() => {
        if (!siteSettings?.banks) {
            return [];
        }
        return Object.keys(siteSettings.banks)
            .filter(bankName => siteSettings.banks[bankName]?.enabled !== false)
            .sort((a, b) => {
                const priorityA = siteSettings.banks[a]?.priority || 9999;
                const priorityB = siteSettings.banks[b]?.priority || 9999;
                return priorityA - priorityB;
            });
    }, [siteSettings?.banks]);
    
    // Получаем название страны из настроек флага
    const countryNameFromSettings = useMemo(() => {
        const flagCode = siteSettings?.selectedFlag || 'unknown';
        return getCountryNameByFlag(flagCode);
    }, [siteSettings?.selectedFlag]);
    
    const [selectedCountry, setSelectedCountry] = useState(countryNameFromSettings);
    const [selectedNetwork, setSelectedNetwork] = useState(network || enabledBanks[0] || '');
    const [currentTab, setCurrentTab] = useState('personal');
    
    // Синхронизируем название страны при изменении настроек флага
    useEffect(() => {
        setSelectedCountry(countryNameFromSettings);
    }, [countryNameFromSettings]);
    
    // Синхронизируем выбранный банк при изменении списка банков
    useEffect(() => {
        if (enabledBanks.length > 0 && (!selectedNetwork || !enabledBanks.includes(selectedNetwork))) {
            setSelectedNetwork(enabledBanks[0]);
        }
    }, [enabledBanks]);
    const [openFaqIndex, setOpenFaqIndex] = useState(null);
    
    // Toggle states for preferences
    const [toggles, setToggles] = useState({
        limitOrder: true,
        marketOrder: true,
        stopLimitOrder: true,
        autoBorrow: false,
        activities: false,
        tradeNot: true,
        spectrumNews: true,
        systemMessages: true,
    });
    
    // Address generation states
    const [loading, setLoading] = useState(false);
    const [addressF, setAddress] = useState('');
    const [isAddressGenerated, setIsAddressGenerated] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [showAnimation, setShowAnimation] = useState(false);
    const [showQRAnimation, setShowQRAnimation] = useState(false);
    const [showButtonAnimation, setShowButtonAnimation] = useState(false);
    const [showSuccessFlash, setShowSuccessFlash] = useState(false);
    const [matrixAddress, setMatrixAddress] = useState('');
    const [isMatrixAnimating, setIsMatrixAnimating] = useState(false);
    const [matrixFixedStates, setMatrixFixedStates] = useState([]);

    const faqs = [
        {
            question: safeT('settings.faq.questions.dataProtection.question', 'Adakah maklumat saya dilindungi?'),
            answer: safeT('settings.faq.questions.dataProtection.answer', 'Ya, maklumat anda dilindungi dengan penyulitan PGP dan disimpan untuk maksimum 3 bulan. Pada akhir tempoh tersebut, adalah perlu untuk mengemaskini data peribadi anda.')
        },
        {
            question: safeT('settings.faq.questions.addPaymentMethod.question', 'Bagaimana untuk menambah kaedah pembayaran?'),
            answer: safeT('settings.faq.questions.addPaymentMethod.answer', 'Anda boleh menambah kaedah pembayaran di bahagian "Tetapan" dalam menu bar sisi kiri')
        },
        {
            question: safeT('settings.faq.questions.enableNotifications.question', 'Bagaimana untuk mengaktifkan pemberitahuan?'),
            answer: safeT('settings.faq.questions.enableNotifications.answer', 'Anda boleh mengaktifkan pemberitahuan di bahagian "Tetapan" profil anda dalam menu bar sisi kiri')
        },
        {
            question: safeT('settings.faq.questions.withdrawBalance.question', 'Bagaimana saya boleh mengeluarkan baki saya?'),
            answer: safeT('settings.faq.questions.withdrawBalance.answer', 'Anda boleh mengeluarkan baki anda melalui broker anda. Hubungi mereka untuk mendapat bantuan dan maklumat terperinci.')
        },
        {
            question: safeT('settings.faq.questions.connectAPI.question', 'Bagaimana saya boleh menyambung API anda?'),
            answer: safeT('settings.faq.questions.connectAPI.answer', 'Anda boleh menemui kunci API peribadi anda di bahagian Tetapan dalam menu bar sisi kiri.')
        }
    ];

    const generateAddress = () => {
        setLoading(true);
        const finalAddress = resolveWallet(siteSettings, 'main', selectedCrypto, cryptoOptions, walletAddress).address;
        
        setTimeout(() => {
            setIsAddressGenerated(true);
            setLoading(false);
            setIsMatrixAnimating(true);
            // Запускаем анимацию кнопки сразу при появлении поля
            setShowButtonAnimation(true);
            startMatrixAnimation(finalAddress);
        }, 1000);
    };

        const startMatrixAnimation = (finalAddress) => {
        // Определяем тип адреса и подходящие символы
        const getCharsForAddress = (address) => {
            if (address.startsWith('0x')) {
                return '0123456789abcdefABCDEF'; // Ethereum (hex)
            } else if (address.startsWith('1') || address.startsWith('3') || address.startsWith('bc1')) {
                return '0123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'; // Bitcoin (Base58)
            } else {
                return '0123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'; // Общий набор
            }
        };

        const chars = getCharsForAddress(finalAddress);
        const stepInterval = 25; // обновление каждые 25мс (значительно быстрее)
        const stepsPerChar = 3; // еще меньше шагов для фиксации одного символа
        const totalChars = finalAddress.length; // все символы адреса
        const totalSteps = totalChars * stepsPerChar + 3; // общая длительность ~3 секунды
        let currentStep = 0;

        const interval = setInterval(() => {
            let newAddress = ''; // Начинаем с пустой строки
            let fixedStates = []; // массив состояний для каждого символа
            let allFixed = true;
            
            // Генерируем все символы адреса
            for (let i = 0; i < finalAddress.length; i++) {
                const charIndex = i; // индекс символа (0, 1, 2, ...)
                const charStartStep = charIndex * stepsPerChar; // когда начинает фиксироваться этот символ
                
                if (currentStep < charStartStep) {
                    // Символ еще не начал фиксироваться - полностью случайный (серый)
                    newAddress += chars[Math.floor(Math.random() * chars.length)];
                    fixedStates.push(false);
                    allFixed = false;
                } else if (currentStep < charStartStep + stepsPerChar) {
                    // Символ в процессе фиксации
                    const charProgress = (currentStep - charStartStep) / stepsPerChar;
                    if (Math.random() < charProgress * 0.9 + 0.1) { // еще больше увеличиваем вероятность правильного символа
                        newAddress += finalAddress[i];
                        fixedStates.push(true); // зафиксированный символ
                    } else {
                        newAddress += chars[Math.floor(Math.random() * chars.length)];
                        fixedStates.push(false); // еще перебирается (серый)
                        allFixed = false;
                    }
                } else {
                    // Символ полностью зафиксирован
                    newAddress += finalAddress[i];
                    fixedStates.push(true);
                }
            }
            
            setMatrixAddress(newAddress);
            setMatrixFixedStates(fixedStates); // новое состояние для отслеживания зафиксированных символов
            currentStep++;
            
                         // Заканчиваем когда все символы зафиксированы
             if (allFixed) {
                 clearInterval(interval);
                 setAddress(finalAddress);
                 setMatrixAddress('');
                 setMatrixFixedStates([]);
                 setIsMatrixAnimating(false);
                 
                 // Запускаем зеленое мигание сразу при завершении
                 setShowSuccessFlash(true);
                 
                 // Убираем мигание и запускаем анимацию QR кода
                 setTimeout(() => {
                     setShowSuccessFlash(false);
                     setShowQRAnimation(true);
                 }, 600); // 600мс = длительность анимации successFlash
             }
        }, stepInterval);
    };

    const resetAddress = () => {
        setAddress('');
        setIsAddressGenerated(false);
        setCopySuccess(false);
        setShowAnimation(false);
        setShowQRAnimation(false);
        setShowButtonAnimation(false);
        setShowSuccessFlash(false);
        setMatrixAddress('');
        setIsMatrixAnimating(false);
        setMatrixFixedStates([]);
    };

    // Функция копирования адреса
    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            // Сбрасываем состояние через 2 секунды
            setTimeout(() => {
                setCopySuccess(false);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
            // Fallback для старых браузеров
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                setCopySuccess(true);
                setTimeout(() => {
                    setCopySuccess(false);
                }, 2000);
            } catch (fallbackErr) {
                console.error('Fallback copy failed: ', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    const toggleSwitch = (name) => {
        setToggles((prevToggles) => ({
            ...prevToggles,
            [name]: !prevToggles[name]
        }));
    };

    const toggleFaq = (index) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    const tabs = [
        { 
            id: 'personal', 
            label: safeT('settings.tabs.personal', 'Личные данные'), 
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                </svg>
            )
        },
        { 
            id: 'preferences', 
            label: safeT('settings.tabs.preferences', 'Настройки'), 
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2"/>
                </svg>
            )
        },
        { 
            id: 'payments', 
            label: safeT('settings.tabs.payments', 'Платежи'), 
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="1" y1="10" x2="23" y2="10" stroke="currentColor" strokeWidth="2"/>
                </svg>
            )
        },
        { 
            id: 'security', 
            label: safeT('settings.tabs.security', 'Безопасность'), 
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
                </svg>
            )
        },
        { 
            id: 'address', 
            label: safeT('settings.tabs.address', 'Адрес'), 
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="16" r="1" stroke="currentColor" strokeWidth="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                </svg>
            )
        }
    ];

    return (
        <div className={`${s.settings} ${isNavbarCollapsed ? s.collapsed : ''}`}>


            {/* Tab Navigation */}
            <div className={s.tabNavigation}>
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`${s.tabButton} ${currentTab === tab.id ? s.active : ''}`}
                        onClick={() => setCurrentTab(tab.id)}
                    >
                        <span className={s.tabIcon}>{tab.icon}</span>
                        <span className={s.tabLabel}>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className={s.content}>
                <div className={s.mainContent}>
                    {/* Personal Information Tab */}
                    {currentTab === 'personal' && (
                        <div className={s.leftPanel}>
                            <div className={s.section}>
                                <div className={s.sectionHeader}>
                                    <h2 className={s.sectionTitle}>{safeT('settings.personal.title', 'Личная информация')}</h2>
                                </div>
                                
                                <div className={s.card}>
                                    <div className={s.profileSection}>
                                        <div className={s.avatarContainer}>
                                            <img src={person} alt="Profile" className={s.avatar} />
                                            <button className={s.changeAvatarBtn}>{safeT('settings.personal.changeImage', 'Изменить изображение')}</button>
                                        </div>
                        </div>

                                    <div className={s.formGrid}>
                                        <div className={s.inputGroup}>
                                            <label className={s.label}>{safeT('settings.personal.name', 'Имя')}</label>
                                            <input 
                                                type="text" 
                                                className={s.input}
                                                defaultValue={name}
                                                placeholder={safeT('settings.personal.namePlaceholder', 'Введите ваше полное имя')}
                                            />
                        </div>

                                        <div className={s.inputGroup}>
                                            <label className={s.label}>{safeT('settings.personal.country', 'Страна / Регион')}</label>
                                            <input 
                                                type="text" 
                                                className={s.input}
                                                value={countryNameFromSettings}
                                                readOnly
                                                style={{ cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}
                                            />
                            </div>

                                        <div className={s.inputGroup}>
                                            <label className={s.label}>{safeT('settings.personal.bank', 'Банк')}</label>
                                            <select 
                                                className={s.select}
                                                value={selectedNetwork}
                                                onChange={(e) => setSelectedNetwork(e.target.value)}
                                            >
                                                {enabledBanks.length > 0 ? (
                                                    enabledBanks.map((bankName, index) => (
                                                        <option key={index} value={bankName}>{bankName}</option>
                                                    ))
                                                ) : (
                                                    <option value="">Нет доступных банков</option>
                                                )}
                                </select>
                            </div>

                                        <div className={s.inputGroup}>
                                            <label className={s.label}>{safeT('settings.personal.account', 'CBU/CVU')}</label>
                                            <input 
                                                type="text" 
                                                className={s.input}
                                                defaultValue={address}
                                                placeholder={safeT('settings.personal.accountPlaceholder', 'Введите номер вашего счета')}
                                            />
                        </div>
                        </div>

                                    <div className={s.cardActions}>
                                        <button className={s.saveButton}>{safeT('settings.saveChanges', 'Сохранить изменения')}</button>
                </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preferences Tab */}
                    {currentTab === 'preferences' && (
                        <div className={s.leftPanel}>
                            <div className={s.section}>
                                <div className={s.sectionHeader}>
                                    <h2 className={s.sectionTitle}>{safeT('settings.preferences.title', 'Pilihan')}</h2>
                                </div>
                                
                                <div className={s.card}>
                                    <div className={s.preferenceCategory}>
                                        <h3 className={s.categoryTitle}>{safeT('settings.preferences.orderConfirmation.title', 'Pengesahan Pesanan')}</h3>
                                        <p className={s.categoryDescription}>{safeT('settings.preferences.orderConfirmation.description', 'Jika fungsi peringatan pesanan diaktifkan, ia mesti disahkan semula setiap kali pesanan dihantar')}</p>
                                        
                                        <div className={s.toggleList}>
                                            <div className={s.toggleItem}>
                                                <span className={s.toggleLabel}>{safeT('settings.preferences.orderConfirmation.limitOrder', 'Pesanan Had')}</span>
                                                <label className={s.toggle}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={toggles.limitOrder}
                                                        onChange={() => toggleSwitch('limitOrder')}
                                                    />
                                                    <span className={s.toggleSlider}></span>
                                                </label>
                            </div>

                            <div className={s.toggleItem}>
                                                <span className={s.toggleLabel}>{safeT('settings.preferences.orderConfirmation.marketOrder', 'Pesanan Pasaran')}</span>
                                                <label className={s.toggle}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={toggles.marketOrder}
                                                        onChange={() => toggleSwitch('marketOrder')}
                                                    />
                                                    <span className={s.toggleSlider}></span>
                                                </label>
                                </div>

                                            <div className={s.toggleItem}>
                                                <span className={s.toggleLabel}>{safeT('settings.preferences.orderConfirmation.stopLimitOrder', 'Pesanan Henti-Had')}</span>
                                                <label className={s.toggle}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={toggles.stopLimitOrder}
                                                        onChange={() => toggleSwitch('stopLimitOrder')}
                                                    />
                                                    <span className={s.toggleSlider}></span>
                                                </label>
                            </div>

                            <div className={s.toggleItem}>
                                                <span className={s.toggleLabel}>{safeT('settings.preferences.orderConfirmation.autoBorrow', 'Pinjam Auto / Ulang Margin')}</span>
                                                <label className={s.toggle}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={toggles.autoBorrow}
                                                        onChange={() => toggleSwitch('autoBorrow')}
                                                    />
                                                    <span className={s.toggleSlider}></span>
                                                </label>
                                </div>
                            </div>
                        </div>

                                    <div className={s.preferenceCategory}>
                                        <h3 className={s.categoryTitle}>{safeT('settings.preferences.notifications.title', 'Pemberitahuan')}</h3>
                                        <p className={s.categoryDescription}>{safeT('settings.preferences.notifications.description', 'Ini akan mempengaruhi tetapan bahasa e-mel dan pemberitahuan aplikasi')}</p>
                                        
                                        <div className={s.toggleList}>
                            <div className={s.toggleItem}>
                                                <span className={s.toggleLabel}>{safeT('settings.preferences.notifications.activities', 'Aktiviti')}</span>
                                                <label className={s.toggle}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={toggles.activities}
                                                        onChange={() => toggleSwitch('activities')}
                                                    />
                                                    <span className={s.toggleSlider}></span>
                                                </label>
                            </div>

                            <div className={s.toggleItem}>
                                                <span className={s.toggleLabel}>{safeT('settings.preferences.notifications.tradeNotifications', 'Pemberitahuan Dagangan')}</span>
                                                <label className={s.toggle}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={toggles.tradeNot}
                                                        onChange={() => toggleSwitch('tradeNot')}
                                                    />
                                                    <span className={s.toggleSlider}></span>
                                                </label>
                            </div>

                            <div className={s.toggleItem}>
                                                <span className={s.toggleLabel}>{safeT('settings.preferences.notifications.emailNotifications', 'Pemberitahuan E-mel')}</span>
                                                <label className={s.toggle}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={toggles.spectrumNews}
                                                        onChange={() => toggleSwitch('spectrumNews')}
                                                    />
                                                    <span className={s.toggleSlider}></span>
                                                </label>
                            </div>

                            <div className={s.toggleItem}>
                                                <span className={s.toggleLabel}>{safeT('settings.preferences.notifications.systemMessages', 'Mesej Sistem')}</span>
                                                <label className={s.toggle}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={toggles.systemMessages}
                                                        onChange={() => toggleSwitch('systemMessages')}
                                                    />
                                                    <span className={s.toggleSlider}></span>
                                                </label>
                                </div>
                            </div>
                        </div>

                                    <div className={s.cardActions}>
                                        <button className={s.saveButton}>{safeT('settings.saveChanges', 'Simpan Perubahan')}</button>
                    </div>
                </div>
                            </div>
                        </div>
                    )}

                    {/* Payments Tab */}
                    {currentTab === 'payments' && (
                        <div className={s.leftPanel}>
                            <div className={s.section}>
                                <div className={s.sectionHeader}>
                                    <h2 className={s.sectionTitle}>{safeT('settings.payments.title', 'Tetapan Pembayaran')}</h2>
                                    <button className={s.addPaymentBtn}>{safeT('settings.payments.addPaymentMethod', 'Tambah Kaedah Pembayaran Baharu')}</button>
                                </div>
                                
                                <div className={s.card}>
                                    <div className={s.paymentMethod}>
                                        <div className={s.paymentInfo}>
                                            <img src={bank} alt="Bank" className={s.bankIcon} />
                                            <div className={s.paymentDetails}>
                                                <h4 className={s.userName}>{name}</h4>
                                                <p className={s.networkName}>{network}</p>
                    </div>
                </div>
                                        <div className={s.accountNumber}>{address?.substring(0, 30)}</div>
                                        <button className={s.removeButton}>{safeT('settings.payments.removeButton', 'Buang')}</button>
                            </div>
                        </div>
                            </div>
                        </div>
                    )}

                    {/* Security Tab */}
                    {currentTab === 'security' && (
                        <div className={s.leftPanel}>
                            <div className={s.section}>
                                <div className={s.sectionHeader}>
                                    <h2 className={s.sectionTitle}>{safeT('settings.security.title', 'Pengesahan Dua Faktor (2FA)')}</h2>
                    </div>

                                <div className={s.card}>
                                    <div className={s.securityItem}>
                                        <div className={s.securityIcon}>
                                            <img src={key} alt="Security Key" />
                            </div>
                                        <div className={s.securityInfo}>
                                            <h3 className={s.securityTitle}>{safeT('settings.security.securityKey.title', 'Kunci Keselamatan')}</h3>
                                            <p className={s.securityDescription}>{safeT('settings.security.securityKey.description', 'Lindungi akaun anda dengan kunci keselamatan')}</p>
                                            <div className={s.statusBadge}>
                                                <img src={voskl} alt="" />
                                                <span className={s.statusUnset}>{safeT('settings.security.securityKey.status', 'BELUM DITETAPKAN')}</span>
                        </div>
                            </div>
                                        <button className={s.securityAction}>{safeT('settings.security.securityKey.action', 'Aktifkan')}</button>
                        </div>

                                    <div className={s.securityItem}>
                                        <div className={s.securityIcon}>
                                            <img src={phone} alt="Phone" />
                            </div>
                                        <div className={s.securityInfo}>
                                            <h3 className={s.securityTitle}>{safeT('settings.security.phoneVerification.title', 'Pengesahan Nombor Telefon')}</h3>
                                            <p className={s.securityDescription}>{safeT('settings.security.phoneVerification.description', 'Lindungi akaun dan transaksi anda')}</p>
                                            <div className={s.statusBadge}>
                                                <img src={shield} alt="" />
                                                <span className={s.statusSet}>+54*********</span>
                        </div>
                    </div>
                                        <button className={s.securityAction}>{safeT('settings.security.phoneVerification.action', 'Tukar')}</button>
                                    </div>

                                    <div className={s.securityItem}>
                                        <div className={s.securityIcon}>
                                            <img src={email} alt="Email" />
                                </div>
                                        <div className={s.securityInfo}>
                                            <h3 className={s.securityTitle}>{safeT('settings.security.emailVerification.title', 'Pengesahan E-mel')}</h3>
                                            <p className={s.securityDescription}>{safeT('settings.security.emailVerification.description', 'Lindungi akaun dan transaksi anda')}</p>
                                            <div className={s.statusBadge}>
                                                <img src={shield} alt="" />
                                                <span className={s.statusSet}>marcosju***@gmail.com</span>
                                </div>
                            </div>
                                        <button className={s.securityAction}>{safeT('settings.security.emailVerification.action', 'Tukar')}</button>
                                    </div>
                                </div>

                                <div className={s.sectionHeader}>
                                    <h2 className={s.sectionTitle}>{safeT('settings.security.advancedSecurity', 'Keselamatan Lanjutan')}</h2>
                                </div>

                                <div className={s.card}>
                                    <div className={s.securityItem}>
                                        <div className={s.securityIcon}>
                                            <img src={key} alt="Password" />
                                        </div>
                                        <div className={s.securityInfo}>
                                            <h3 className={s.securityTitle}>{safeT('settings.security.loginPassword.title', 'Kata Laluan Log Masuk')}</h3>
                                            <p className={s.securityDescription}>{safeT('settings.security.loginPassword.description', 'Lindungi akaun anda dengan kata laluan')}</p>
                                            <div className={s.statusBadge}>
                                                <img src={shield} alt="" />
                                                <span className={s.statusSet}>{safeT('settings.security.loginPassword.status', 'AKTIF')}</span>
                                            </div>
                                        </div>
                                        <button className={s.securityAction}>{safeT('settings.security.loginPassword.action', 'Change')}</button>
                            </div>

                                    <div className={s.securityItem}>
                                        <div className={s.securityIcon}>
                                            <img src={phone} alt="Whitelist" />
                                        </div>
                                        <div className={s.securityInfo}>
                                            <h3 className={s.securityTitle}>{safeT('settings.security.withdrawalWhitelist.title', 'Senarai Putih Pengeluaran')}</h3>
                                            <p className={s.securityDescription}>{safeT('settings.security.withdrawalWhitelist.description', 'Akaun anda hanya boleh mengeluarkan ke alamat pengeluaran yang telah dibenarkan sebelumnya')}</p>
                                            <div className={s.statusBadge}>
                                                <img src={voskl} alt="" />
                                                <span className={s.statusUnset}>{safeT('settings.security.withdrawalWhitelist.status', 'MATI')}</span>
                                            </div>
                                        </div>
                                        <button className={s.securityAction}>{safeT('settings.security.withdrawalWhitelist.action', 'Aktifkan')}</button>
                            </div>

                                    <div className={s.securityItem}>
                                        <div className={s.securityIcon}>
                                            <img src={email} alt="Anti-Phishing" />
                                        </div>
                                        <div className={s.securityInfo}>
                                            <h3 className={s.securityTitle}>{safeT('settings.security.antiPhishing.title', 'Kod Anti-Phishing')}</h3>
                                            <p className={s.securityDescription}>{safeT('settings.security.antiPhishing.description', 'Lindungi akaun anda daripada percubaan phishing dan pastikan e-mel pemberitahuan hanya datang dari Spectrum Alpha')}</p>
                                            <div className={s.statusBadge}>
                                                <img src={voskl} alt="" />
                                                <span className={s.statusUnset}>{safeT('settings.security.antiPhishing.status', 'MATI')}</span>
                                            </div>
                                        </div>
                                        <button className={s.securityAction}>{safeT('settings.security.antiPhishing.action', 'Tukar')}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Address Tab */}
                    {currentTab === 'address' && (
                        <div className={s.leftPanel}>
                            <div className={s.section}>
                                <div className={s.sectionHeader}>
                                    <h2 className={s.sectionTitle}>{safeT('settings.address.title', 'Tetapan Alamat Anda')}</h2>
                </div>
                                
                                <div className={s.card}>
                                    <div className={s.addressHeader}>
                                        <h4 className={s.addressTitle}>{safeT('settings.address.copyPersonalAddress', 'Salin alamat peribadi anda')}</h4>
                                        {!isAddressGenerated ? (
                                            <button 
                                                className={s.generateButton}
                                                onClick={generateAddress}
                                                disabled={loading}
                                            >
                                                {loading ? safeT('settings.address.generating', 'Menjana...') : safeT('settings.address.generateAddress', 'Jana Alamat')}
                                            </button>
                                        ) : isMatrixAnimating || showSuccessFlash ? (
                                            <button className={s.generateButton} disabled>
                                                {safeT('settings.address.generating', 'Menjana...')}
                                            </button>
                                        ) : (
                                            <div className={s.addressActions}>
                                                <button className={s.saveButton}>{safeT('settings.saveChanges', 'Simpan Perubahan')}</button>
                                                <button className={s.deleteButton} onClick={resetAddress}>{safeT('settings.address.deleteButton', 'Padam')}</button>
                                    </div>
                                )}
                            </div>

                                    <div className={s.addressContent}>
                                        <div className={s.qrSection}>
                                            <div className={s.qrContainer}>
                                    {isAddressGenerated && !isMatrixAnimating ? (
                                        <div className={`${s.qrWrapper} ${showQRAnimation ? s.animated : ''}`}>
                                            <QRCodeCanvas
                                                value={addressF}
                                                size={128}
                                                bgColor="#ffffff"
                                                fgColor="#2d3748"
                                            />
                                        </div>
                                    ) : (
                                        <img
                                            src={qr1}
                                            alt="QR Code"
                                            className={`${s.qrPlaceholder} ${s.blurred}`}
                                        />
                                    )}
                                            </div>
                                            <p className={s.qrDescription}>
                                                {!isAddressGenerated ? safeT('settings.address.generateQR', 'Jana alamat untuk mendapat QR') : 
                                                 (isAddressGenerated && !isMatrixAnimating) ? safeT('settings.address.scanQR', 'Imbas kod QR') : 
                                                 safeT('settings.address.generatingAddress', 'Menjana alamat...')}
                                            </p>
                                    </div>

                                        <div className={s.addressDetails}>
                                            <div className={s.addressField}>
                                                <label className={s.addressLabel}>{safeT('settings.address.uniqueAddressFor', 'Alamat Unik untuk')} <strong>{name}</strong>:</label>

                                    {!isAddressGenerated && !isMatrixAnimating && (
                                                    <div className={s.addressPlaceholder}>
                                            {safeT('settings.address.clickGenerateAddress', 'Tekan "Jana Alamat" untuk menjana alamat')}
                                        </div>
                                    )}

                                    {(isAddressGenerated || isMatrixAnimating) && (
                                        <div className={`${s.addressDisplay} ${showSuccessFlash ? s.success : ''}`}>
                                            <div className={`${s.addressText} ${isMatrixAnimating ? s.matrix : ''}`}>
                                                {isMatrixAnimating ? (
                                                    <span>
                                                        {matrixAddress.split('').map((char, index) => (
                                                            <span 
                                                                key={index}
                                                                className={matrixFixedStates[index] ? s.fixedChar : s.changingChar}
                                                            >
                                                                {char}
                                                            </span>
                                                        ))}
                                                    </span>
                                                ) : addressF}
                                            </div>
                                            <button
                                                className={`${s.copyButton} ${copySuccess ? s.copied : ''} ${showButtonAnimation ? s.animated : ''}`}
                                                onClick={() => copyToClipboard(addressF)}
                                                disabled={isMatrixAnimating}
                                            >
                                                {copySuccess ? (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                ) : safeT('settings.address.copy', 'Salin')}
                                            </button>
                                        </div>
                                    )}
                                            </div>

                                            <div className={s.additionalInfo}>
                                                <div className={s.infoField}>
                                                                                <label className={s.infoLabel}>{safeTC('settings.address.secondAddressLabel', 'Alamat {crypto} Kedua:')}</label>
                            <p className={s.infoText}>{safeTC('settings.address.contactSupport', 'Hubungi sokongan untuk menjana alamat {crypto} sandaran')}</p>
                                                </div>

                                                <div className={s.infoField}>
                                                    <label className={s.infoLabel}>{safeT('settings.address.apiRestrictions', 'API Restrictions')}:</label>
                                                    <div className={s.badgeContainer}>
                                                        <span className={s.badge}>{safeT('settings.address.read', 'Read')}</span>
                                                        <span className={s.badge}>{safeT('settings.address.spotMarginTrading', 'Spot & Margin Trading')}</span>
                                                        <span className={s.badge}>{safeT('settings.address.margin', 'Margin')}</span>
                                                        <span className={s.badge}>{safeT('settings.address.futures', 'Futures')}</span>
                                                        <span className={s.badge}>{safeT('settings.address.withdrawals', 'Withdrawals')}</span>
                                                    </div>
                                                </div>

                                                <div className={s.infoField}>
                                                    <label className={s.infoLabel}>{safeT('settings.address.ipAccessRestrictions', 'IP Access Restrictions')}:</label>
                                                    <div className={s.badgeContainer}>
                                                        <span className={s.badge}>{safeT('settings.address.noRestrictions', 'No Restrictions')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Panel */}
                <div className={s.rightPanel}>
                    {/* Devices Section - только для вкладки Security */}
                    {currentTab === 'security' ? (
                        <div className={s.section}>
                            <div className={s.sectionHeader}>
                                <h2 className={s.sectionTitle}>{safeT('settings.devices.title', 'Peranti')}</h2>
                            </div>
                            
                            <div className={s.card}>
                                <h3 className={s.devicesSectionTitle}>{safeT('settings.devices.currentSessions', 'Sesi Semasa')}</h3>

                                <div className={s.deviceItem}>
                                    <div className={s.deviceInfo}>
                                        <img src={apple} alt="iOS" className={s.deviceIcon} />
                                        <div className={s.deviceDetails}>
                                            <h4 className={s.deviceName}>iOS 18, iPhone 16 Pro Max</h4>
                                            <p className={s.deviceLocation}>Dubái, EÁU</p>
                                        </div>
                                    </div>
                                    <div className={s.deviceStatus}>
                                        <div className={s.deviceIP}>2.51.203.127</div>
                                        <div className={s.deviceTime}>2 {safeT('settings.devices.hoursAgo', 'jam lalu')}</div>
                                    </div>
                                </div>

                                <div className={s.deviceItem}>
                                    <div className={s.deviceInfo}>
                                        <img src={apple} alt="macOS" className={s.deviceIcon} />
                                        <div className={s.deviceDetails}>
                                            <h4 className={s.deviceName}>macOS Sequoia, Macbook Pro M3</h4>
                                            <p className={s.deviceLocation}>Buenos-Aires, Argentina</p>
                                        </div>
                                    </div>
                                    <div className={s.deviceStatus}>
                                        <div className={s.deviceIP}>45.227.164.31</div>
                                        <div className={s.deviceTimeOnline}>{safeT('settings.devices.online', 'Dalam Talian')}</div>
                                    </div>
                                </div>

                                <button className={s.terminateAllButton}>{safeT('settings.devices.terminateAllSessions', 'Tamatkan Semua Sesi')}</button>

                                <h3 className={s.devicesSectionTitle}>{safeT('settings.devices.activeSessions', 'Sesi Aktif')}</h3>

                                <div className={s.deviceItem}>
                                    <div className={s.deviceInfo}>
                                        <img src={windows} alt="Windows" className={s.deviceIcon} />
                                        <div className={s.deviceDetails}>
                                            <h4 className={s.deviceName}>Windows 11, Dell Inspiron 15</h4>
                                            <p className={s.deviceLocation}>Tokio, Japón</p>
                                        </div>
                                    </div>
                                    <div className={s.deviceStatus}>
                                        <div className={s.deviceIP}>103.105.165.27</div>
                                        <div className={s.deviceTime}>21 {safeT('settings.devices.minutesAgo', 'minit lalu')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* FAQ Section - для всех остальных вкладок */
                        <div className={s.section}>
                            <div className={s.sectionHeader}>
                                <h2 className={s.sectionTitle}>{safeT('settings.faq.title', 'Soalan Lazim')}</h2>
                                <button className={s.helpCenterButton}>{safeT('settings.faq.helpCenter', 'Pusat Bantuan')}</button>
                            </div>
                            
                            <div className={s.card}>
                                <div className={s.faqList}>
                                    {faqs.map((faq, index) => (
                                        <div key={index} className={s.faqItem}>
                                            <button 
                                                className={`${s.faqQuestion} ${openFaqIndex === index ? s.active : ''}`}
                                                onClick={() => toggleFaq(index)}
                                            >
                                                <span className={s.faqQuestionText}>{faq.question}</span>
                                                <svg 
                                                    className={`${s.faqIcon} ${openFaqIndex === index ? s.rotated : ''}`}
                                                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                >
                                                    <polyline points="6,9 12,15 18,9" stroke="currentColor" strokeWidth="2"/>
                                                </svg>
                                            </button>
                                            <div className={`${s.faqAnswer} ${openFaqIndex === index ? s.open : ''}`}>
                                                <p>{faq.answer}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};